import React, { FC, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { money } from '../data/format';
import type { ISarDraft, ISarParty } from '../data/types';

/**
 * THE SAR, AS IT WILL BE FILED — opened from the top of the transaction drawer.
 *
 * Everything that goes into the FinCEN filing is on this one screen, because
 * an officer signs off on what is filed, not on a summary of it:
 *
 *   Part I   the subject — pre-filled from the verified onboarding (KYC)
 *            record where one is linked, editable, each value tagged with its
 *            source;
 *   Part II  the suspicious activity — amount, dates, and every classification
 *            with the MAPPED REASON CODES: which monitoring control put which
 *            FinCEN type/subtype on the filing, and why;
 *   III/IV   the institutions and contacts, exactly as they will be filed;
 *   Part V   the narrative — the description FinCEN reads — in full, editable.
 *
 * Nothing is filed from here. "Generate XML" produces a review copy, validated
 * against FinCEN's schema server-side. A full SSN typed below travels in that
 * one request and is never saved; onboarding keeps only the last four.
 */

const fmtYmd = (s?: string | null) =>
	s && /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s ?? '';

const ROLE_LABEL: Record<ISarParty['role'], string> = {
	subject: 'Subject',
	transmitter: 'Transmitter',
	transmitterContact: 'Transmitter contact',
	filingInstitution: 'Filing institution',
	activityInstitution: 'Institution where activity occurred',
	contactOffice: 'Contact office',
};

const ID_TYPES: [string, string][] = [
	['5', "Driver's license / State ID"],
	['6', 'Passport'],
	['7', 'Alien registration'],
	['9', 'Foreign'],
	['999', 'Other'],
];
const ID_LABEL: Record<string, string> = {
	'1': 'SSN / ITIN',
	'2': 'EIN',
	'4': 'TIN',
	'28': 'Transmitter Control Code',
	...Object.fromEntries(ID_TYPES),
};

const SOURCE: Record<string, string> = {
	transaction: 'record',
	kyc: 'KYC',
	business: 'business',
	'filer-profile': 'filer profile',
	officer: 'officer',
	'rule-map': 'rule map',
	derived: 'derived',
	'system-clock': 'today',
};

interface SubjectForm {
	firstName: string;
	middleName: string;
	lastName: string;
	birthDate: string;
	ssn: string;
	idNumber: string;
	idTypeCode: string;
	street: string;
	city: string;
	state: string;
	zip: string;
	country: string;
}

const fromDraft = (d: ISarDraft): SubjectForm => {
	const s = d.activity.parties.find(p => p.role === 'subject');
	const doc = s?.identifications?.find(i => i.typeCode !== '1');
	return {
		firstName: s?.name?.first ?? '',
		middleName: s?.name?.middle ?? '',
		lastName: s?.name?.last ?? s?.name?.full ?? '',
		birthDate: s?.birthDate ? fmtYmd(s.birthDate) : '',
		ssn: '',
		idNumber: doc?.number ?? '',
		idTypeCode: doc?.typeCode ?? '5',
		street: s?.address?.street ?? '',
		city: s?.address?.city ?? '',
		state: s?.address?.state ?? '',
		zip: s?.address?.zip ?? '',
		country: s?.address?.country ?? '',
	};
};

const PartyBlock: FC<{ p: ISarParty }> = ({ p }) => {
	const name = p.name?.full ?? [p.name?.first, p.name?.middle, p.name?.last].filter(Boolean).join(' ');
	const addr = p.address
		? [p.address.street, p.address.city, p.address.state, p.address.zip, p.address.country].filter(Boolean).join(', ')
		: '';
	const ids = [...(p.identification?.number ? [p.identification] : []), ...(p.identifications ?? [])];
	const miss = <span className="sm-miss">not held</span>;
	return (
		<div className="sm-party" data-qa={`sar-party-${p.role}`}>
			<div className="sm-party-h">{ROLE_LABEL[p.role]}</div>
			<table className="sm-kv">
				<tbody>
					<tr>
						<td>name</td>
						<td>{name || miss}</td>
					</tr>
					{p.role !== 'transmitterContact' && (
						<tr>
							<td>address</td>
							<td>{addr || miss}</td>
						</tr>
					)}
					{p.birthDate && (
						<tr>
							<td>date of birth</td>
							<td>{fmtYmd(p.birthDate)}</td>
						</tr>
					)}
					{ids.map(i => (
						<tr key={`${i.typeCode}-${i.number}`}>
							<td>{ID_LABEL[i.typeCode ?? ''] ?? `id type ${i.typeCode}`}</td>
							<td className="mono">
								{i.typeCode === '1' ? `***-**-${String(i.number).slice(-4)}` : i.number}
								{'issuerState' in i && (i as any).issuerState ? ` · ${(i as any).issuerState}` : ''}
								{'issuerCountry' in i && (i as any).issuerCountry ? ` · ${(i as any).issuerCountry}` : ''}
							</td>
						</tr>
					))}
					{p.tinUnknown && (
						<tr>
							<td>TIN</td>
							<td>filed as unknown</td>
						</tr>
					)}
					{p.phone && (
						<tr>
							<td>phone</td>
							<td className="mono">{p.phone}</td>
						</tr>
					)}
					{!ids.length && !p.tinUnknown && p.role !== 'transmitterContact' && p.role !== 'contactOffice' && (
						<tr>
							<td>identification</td>
							<td>{miss}</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
};

export const SarModal: FC<{
	draft: ISarDraft;
	onClose: () => void;
	onSave: (
		caseId: string,
		subject: Record<string, unknown>,
		extra: { narrative?: string; kycSessionId?: string | null }
	) => Promise<unknown>;
	onGenerateXml: (caseId: string, subject: Record<string, unknown>, narrative: string) => Promise<ISarDraft | null>;
}> = ({ draft, onClose, onSave, onGenerateXml }) => {
	const [form, setForm] = useState<SubjectForm>(() => fromDraft(draft));
	const [narrative, setNarrative] = useState(draft.activity.narrative);
	const [saving, setSaving] = useState(false);
	const [xmlState, setXmlState] = useState<{ ok: boolean; text: string } | null>(null);
	const [linkId, setLinkId] = useState('');
	/* ONLY WHAT THE OFFICER TOUCHED is sent as theirs. The form opens pre-filled
	   from records; sending every field would save the derived and KYC values as
	   "officer" overrides — which then outrank a better source forever (linking
	   the KYC record left the transaction's split name in place) and mislabel
	   where the value came from. */
	const [dirty, setDirty] = useState<Set<keyof SubjectForm>>(new Set());
	const [narrativeDirty, setNarrativeDirty] = useState(false);

	/* Re-seed when the server hands back a different draft (a new KYC link, a
	   save) — but never over what the officer is mid-way through typing: only
	   when the underlying case or linked session actually changes. */
	const seedKey = `${draft.caseId}|${draft.kyc.sessionId}`;
	useEffect(() => {
		setForm(f => ({ ...fromDraft(draft), ssn: f.ssn }));
		setNarrative(draft.activity.narrative);
		setDirty(new Set());
		setNarrativeDirty(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [seedKey]);

	useEffect(() => {
		const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
		window.addEventListener('keydown', esc);
		return () => window.removeEventListener('keydown', esc);
	}, [onClose]);

	const src = (label: string) => {
		const f = draft.fields.find(x => x.label === label);
		return f ? <span className="sm-src">{SOURCE[f.source] ?? f.source}</span> : null;
	};
	const subjectPayload = (withSsn: boolean) => {
		const out: Record<string, unknown> = {};
		const take = (k: keyof SubjectForm, as?: string) => {
			if (dirty.has(k)) out[as ?? k] = form[k] || undefined;
		};
		take('firstName');
		take('middleName');
		take('lastName');
		take('birthDate');
		if (dirty.has('idNumber') || dirty.has('idTypeCode')) {
			out.idNumber = form.idNumber || undefined;
			out.idTypeCode = form.idTypeCode || undefined;
		}
		if (['street', 'city', 'state', 'zip', 'country'].some(k => dirty.has(k as keyof SubjectForm))) {
			out.address = { street: form.street, city: form.city, state: form.state, zip: form.zip, country: form.country };
		}
		if (withSsn && form.ssn) out.ssn = form.ssn;
		return out;
	};

	const save = async (kycSessionId?: string | null) => {
		if (!draft.caseId) return;
		setSaving(true);
		await onSave(draft.caseId, subjectPayload(false), {
			// Only a narrative the officer edited is saved as theirs.
			...(narrativeDirty ? { narrative } : {}),
			...(kycSessionId !== undefined ? { kycSessionId } : {}),
		});
		setDirty(new Set());
		setNarrativeDirty(false);
		setSaving(false);
	};

	const generate = async () => {
		if (!draft.caseId) return;
		setSaving(true);
		const d = await onGenerateXml(draft.caseId, subjectPayload(true), narrative);
		setSaving(false);
		if (d?.xml) {
			/* No subject name in the filename — a SAR's existence is confidential
			   (31 CFR 1020.320(e)) and FinCEN asks for exactly this. */
			const blob = new Blob([d.xml], { type: 'application/xml' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `SARX-review-${draft.caseNumber ?? 'draft'}.xml`;
			a.click();
			URL.revokeObjectURL(url);
			setXmlState({ ok: true, text: 'Schema-valid review copy downloaded. Nothing was filed.' });
		} else {
			const why = [...(d?.gaps ?? []).map(g => g.field), ...(d?.validation ?? [])];
			setXmlState({ ok: false, text: `Not complete yet — ${why.slice(0, 6).join('; ')}${why.length > 6 ? '…' : ''}` });
		}
	};

	const set = (k: keyof SubjectForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		setForm(f => ({ ...f, [k]: e.target.value }));
		if (k !== 'ssn') setDirty(d => new Set(d).add(k));
	};

	const a = draft.activity;
	const ssnHint = useMemo(
		() => draft.fields.find(f => f.label === 'SSN / TIN')?.value ?? null,
		[draft.fields]
	);
	const others = a.parties.filter(p => p.role !== 'subject');
	const chip = draft.status === 'ready' ? 'pass' : draft.status === 'needs_information' ? 'review' : 'test';

	return createPortal(
		<div className="sv2-scope sv2 txn-deck">
			<div className="sm-backdrop" onClick={onClose} />
			<div className="sm-panel" role="dialog" aria-modal="true" aria-label="Suspicious Activity Report" data-qa="sar-modal">
				<header className="sm-head">
					<div>
						<div className="sm-kick">FinCEN Suspicious Activity Report · draft</div>
						<h2>
							{draft.caseNumber ? `Case ${draft.caseNumber}` : 'Not escalated'}{' '}
							<span className={`nd ${chip}`} data-qa="sar-modal-status">
								{draft.status.replace('_', ' ')}
							</span>
						</h2>
					</div>
					<button className="dx" type="button" onClick={onClose} aria-label="Close SAR">
						✕
					</button>
				</header>

				<div className="sm-body">
					<div className={`sm-note${draft.recommended ? ' rec' : ''}`}>{draft.recommendation}</div>

					<div className="sm-strip">
						<div>
							<span>amount</span>
							<b>{a.totalAmount !== undefined ? money(a.totalAmount, 'USD') : 'unknown'}</b>
						</div>
						<div>
							<span>activity</span>
							<b>
								{fmtYmd(a.activityFromDate)}
								{a.activityToDate ? ` → ${fmtYmd(a.activityToDate)}` : ''}
							</b>
						</div>
						<div>
							<span>filing date</span>
							<b>{fmtYmd(a.filingDate)}</b>
						</div>
						<div>
							<span>transactions</span>
							<b>{draft.transactions.length}</b>
						</div>
						<div>
							<span>controls</span>
							<b>{draft.rules.length}</b>
						</div>
					</div>

					{(draft.gaps.length > 0 || draft.advisories.length > 0) && (
						<div className="sm-gaps" data-qa="sar-modal-gaps">
							{draft.gaps.length > 0 && (
								<>
									<div className="sm-sub">Still needed before this can be filed ({draft.gaps.length})</div>
									<ul>
										{draft.gaps.map(g => (
											<li key={g.field}>
												<b>{g.field}</b> — {g.why}
											</li>
										))}
									</ul>
								</>
							)}
							{draft.advisories.map(g => (
								<div key={g.field} className="sm-adv">
									<b>{g.field}</b> — {g.why}
								</div>
							))}
						</div>
					)}

					{/* ------------------------------------------------ Part I */}
					<section className="sm-sec">
						<h3>Part I — Subject information</h3>
						<div className="sm-kyc" data-qa="sar-modal-kyc">
							{draft.kyc.linked ? (
								<>
									Pre-filled from onboarding record <span className="mono">{draft.kyc.sessionId}</span>
									{draft.kyc.how ? ` · ${draft.kyc.how}` : ''}
								</>
							) : draft.kyc.candidates.length ? (
								<>
									<div>{draft.kyc.how ?? 'Possible onboarding records for this subject'} — confirm the right person:</div>
									<ul className="sm-cands">
										{draft.kyc.candidates.slice(0, 6).map(c => (
											<li key={c.sessionId}>
												<span>
													{c.name ?? '—'} · {c.email ?? '—'} ·{' '}
													<span className="mono">{c.sessionId}</span>
												</span>
												<button className="dbtn" type="button" disabled={saving || !draft.caseId} onClick={() => save(c.sessionId)}>
													Link
												</button>
											</li>
										))}
									</ul>
								</>
							) : (
								<>{draft.kyc.how ?? 'No onboarding record is linked to this subject.'}</>
							)}
							<div className="sm-link">
								<input
									placeholder="Link an onboarding session by id"
									value={linkId}
									onChange={e => setLinkId(e.target.value.trim())}
								/>
								<button className="dbtn" type="button" disabled={!linkId || saving || !draft.caseId} onClick={() => save(linkId)}>
									Link session
								</button>
							</div>
						</div>

						<div className="sm-grid">
							<label>
								<span>Last name {src('Name')}</span>
								<input value={form.lastName} onChange={set('lastName')} />
							</label>
							<label>
								<span>First name</span>
								<input value={form.firstName} onChange={set('firstName')} />
							</label>
							<label>
								<span>Middle name</span>
								<input value={form.middleName} onChange={set('middleName')} />
							</label>
							<label>
								<span>Date of birth {src('Date of birth')}</span>
								<input type="date" value={form.birthDate} onChange={set('birthDate')} />
							</label>
							<label>
								<span>Identifying document {src('Identifying document')}</span>
								<input value={form.idNumber} onChange={set('idNumber')} />
							</label>
							<label>
								<span>Document type</span>
								<select value={form.idTypeCode} onChange={set('idTypeCode')}>
									{/* FinCEN PartyIdentificationTypeCode — 5 is a licence,
									    6 a passport; these were once swapped in this UI. */}
									{ID_TYPES.map(([v, l]) => (
										<option key={v} value={v}>
											{l}
										</option>
									))}
								</select>
							</label>
							<label className="wide">
								<span>
									SSN / ITIN {src('SSN / TIN')}
									<em>{ssnHint ? ` · on record: ${ssnHint}` : ''}</em>
								</span>
								<input
									value={form.ssn}
									onChange={set('ssn')}
									placeholder="Type the full number to include it — used for this XML only, never saved"
									autoComplete="off"
									inputMode="numeric"
								/>
							</label>
							<label className="wide">
								<span>Street {src('Address')}</span>
								<input value={form.street} onChange={set('street')} />
							</label>
							<label>
								<span>City</span>
								<input value={form.city} onChange={set('city')} />
							</label>
							<label>
								<span>State</span>
								<input value={form.state} onChange={set('state')} />
							</label>
							<label>
								<span>ZIP</span>
								<input value={form.zip} onChange={set('zip')} />
							</label>
							<label>
								<span>Country {src('Country')}</span>
								<input value={form.country} onChange={set('country')} />
							</label>
						</div>
					</section>

					{/* ----------------------------------------------- Part II */}
					<section className="sm-sec">
						<h3>Part II — Suspicious activity, with the mapped reason codes</h3>
						<table className="sm-tbl" data-qa="sar-modal-classifications">
							<thead>
								<tr>
									<th>FinCEN type</th>
									<th>FinCEN subtype</th>
									<th>Other text</th>
									<th>Put there by</th>
								</tr>
							</thead>
							<tbody>
								{draft.classificationDetails.map(c => (
									<tr key={c.subtypeId}>
										<td>
											<span className="mono">{c.typeId}</span> {c.typeLabel}
										</td>
										<td>
											<span className="mono">{c.subtypeId}</span> {c.subtypeLabel}
										</td>
										<td>{c.otherText ?? '—'}</td>
										<td className="mono">{c.ruleUuids.join(', ') || '—'}</td>
									</tr>
								))}
								{!draft.classificationDetails.length && (
									<tr>
										<td colSpan={4}>No classification — no ACTIVE control matched.</td>
									</tr>
								)}
							</tbody>
						</table>

						<div className="sm-sub">How each control maps to the filing</div>
						<table className="sm-tbl" data-qa="sar-modal-reason-codes">
							<thead>
								<tr>
									<th>Control</th>
									<th>Mode</th>
									<th>Maps to</th>
									<th>Why this code</th>
									<th>Evidence</th>
								</tr>
							</thead>
							<tbody>
								{draft.rules.map(r => (
									<tr key={r.ruleUuid}>
										<td>
											{r.ruleName}
											<div className="mono sm-faint">{r.ruleUuid}</div>
										</td>
										<td>{r.mode === 'TEST' ? 'test — not filed' : `active +${r.scoreDelta}`}</td>
										<td>
											{r.typeId ? (
												<>
													<span className="mono">
														{r.typeId}/{r.subtypeId}
													</span>{' '}
													{r.classificationLabel}
												</>
											) : (
												'—'
											)}
										</td>
										<td>{r.rationale ?? '—'}</td>
										<td className="mono sm-ev">
											{Object.entries(r.evidence ?? {})
												.map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
												.join(' · ') || '—'}
										</td>
									</tr>
								))}
							</tbody>
						</table>

						<div className="sm-sub">Transactions in this filing, with the engine&apos;s decision reason</div>
						<table className="sm-tbl" data-qa="sar-modal-transactions">
							<thead>
								<tr>
									<th>Date</th>
									<th>Reference</th>
									<th>Amount</th>
									<th>USD</th>
									<th>Outcome</th>
									<th>Reason code</th>
								</tr>
							</thead>
							<tbody>
								{draft.transactions.map(t => (
									<tr key={t._id}>
										<td>{t.date}</td>
										<td className="mono">{t.txnId}</td>
										<td>
											{t.amount} {t.currency}
										</td>
										<td>{t.usd !== null ? money(t.usd, 'USD') : '—'}</td>
										<td>
											{t.status} · {t.score}
										</td>
										<td>
											{t.decisionReasonCode ? (
												<>
													<span className="mono">{t.decisionReasonCode}</span>
													{t.decisionReasonLabel ? ` — ${t.decisionReasonLabel}` : ''}
												</>
											) : (
												'—'
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</section>

					{/* ------------------------------------------ Parts III/IV */}
					<section className="sm-sec">
						<h3>Parts III &amp; IV — Institutions and contacts, as filed</h3>
						<div className="sm-parties">
							{others.map(p => (
								<PartyBlock key={p.role} p={p} />
							))}
						</div>
					</section>

					{/* ------------------------------------------------ Part V */}
					<section className="sm-sec">
						<h3>
							Part V — Narrative (the description FinCEN reads){' '}
							<span className="sm-faint">
								{narrative.length.toLocaleString()} / 20,000 characters
							</span>
						</h3>
						<textarea
							className="sm-narr"
							value={narrative}
							onChange={e => {
								setNarrative(e.target.value);
								setNarrativeDirty(true);
							}}
							data-qa="sar-modal-narrative"
						/>
					</section>

					{xmlState && <div className={`sm-note ${xmlState.ok ? 'ok' : 'bad'}`}>{xmlState.text}</div>}
				</div>

				<footer className="sm-foot">
					<span className="sm-faint">Nothing is filed from here. A typed SSN is never saved.</span>
					<button className="dbtn" type="button" onClick={onClose}>
						Close
					</button>
					<button className="dbtn" type="button" disabled={saving || !draft.caseId} onClick={() => save()} data-qa="sar-modal-save">
						{saving ? 'Working…' : 'Save to case'}
					</button>
					<button className="dbtn mag" type="button" disabled={saving || !draft.caseId} onClick={generate} data-qa="sar-modal-xml">
						Generate XML (review copy)
					</button>
				</footer>
			</div>
		</div>,
		document.body
	);
};
